/**
 * Support Chat Screen — full-screen real-time chat with support operators.
 */

import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  Text,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  TextInput,
  Modal,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useTheme, type ThemeColors } from '../src/utils/theme';
import { FONT_SIZE, SPACING, RADIUS } from '../src/utils/design-tokens';
import { useSupportStore, type SupportMessage } from '../src/stores/supportStore';
import { useBackHandler } from '../src/navigation/useBackHandler';

export default function SupportChatScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const flatListRef = useRef<FlatList>(null);
  const { colors } = useTheme();
  const s = useMemo(() => createStyles(colors), [colors]);

  const session = useSupportStore((s) => s.session);
  const messages = useSupportStore((s) => s.messages);
  const status = useSupportStore((s) => s.status);
  const operatorName = useSupportStore((s) => s.operatorName);
  const operatorTyping = useSupportStore((s) => s.operatorTyping);
  const openSession = useSupportStore((s) => s.openSession);
  const loadActiveSession = useSupportStore((s) => s.loadActiveSession);
  const sendMessage = useSupportStore((s) => s.sendMessage);
  const closeSession = useSupportStore((s) => s.closeSession);
  const rateSession = useSupportStore((s) => s.rateSession);
  const clearSupport = useSupportStore((s) => s.clearSupport);
  const initWebSocket = useSupportStore((s) => s.initWebSocket);
  const cleanupWebSocket = useSupportStore((s) => s.cleanupWebSocket);

  const [inputText, setInputText] = useState('');
  const [showRating, setShowRating] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [showEndConfirm, setShowEndConfirm] = useState(false);

  useEffect(() => {
    initWebSocket();
    // If we already have a session, reload it; otherwise open new one
    if (session && (session.status === 'open' || session.status === 'pending')) {
      loadActiveSession();
    } else if (!session) {
      openSession().catch(() => {});
    }
    return () => cleanupWebSocket();
  }, []);

  useEffect(() => {
    // Show rating prompt when session closes
    if (status === 'closed' && session && !session.rating) {
      setShowRating(true);
    }
  }, [status]);

  useEffect(() => {
    // Auto-scroll on new messages
    if (messages.length > 0) {
      setTimeout(() => flatListRef.current?.scrollToEnd({ animated: true }), 100);
    }
  }, [messages.length]);

  const handleSend = useCallback(() => {
    const text = inputText.trim();
    if (!text) return;
    setInputText('');
    sendMessage(text);
  }, [inputText, sendMessage]);

  const handleEndSession = useCallback(() => {
    setShowEndConfirm(true);
  }, []);

  const confirmEndSession = useCallback(() => {
    setShowEndConfirm(false);
    closeSession();
  }, [closeSession]);

  const handleRate = useCallback((rating: number) => {
    setSelectedRating(rating);
    rateSession(rating);
    setShowRating(false);
  }, [rateSession]);

  const handleBack = useCallback(() => {
    if (showEndConfirm) {
      setShowEndConfirm(false);
      return;
    }
    if (showRating) {
      setShowRating(false);
      return;
    }
    if (status === 'closed' || status === 'idle') {
      clearSupport();
    }
    router.back();
  }, [clearSupport, router, showEndConfirm, showRating, status]);

  useBackHandler({
    id: 'support-chat',
    label: 'Support chat',
    priority: 900,
    enabled: true,
    onBack: () => {
      handleBack();
      return true;
    },
  });

  const renderMessage = useCallback(({ item }: { item: SupportMessage }) => {
    const isUser = item.sender_type === 'user';
    const isSystem = item.sender_type === 'system';

    if (isSystem) {
      return (
        <View style={s.systemMsgRow}>
          <Text style={s.systemMsg}>{item.body}</Text>
        </View>
      );
    }

    return (
      <View style={[s.bubbleRow, isUser ? s.bubbleRowRight : s.bubbleRowLeft]}>
        <View style={[s.bubble, isUser ? s.bubbleUser : s.bubbleOperator]}>
          <Text style={[s.bubbleText, isUser ? s.bubbleTextUser : s.bubbleTextOp]}>{item.body}</Text>
          <View style={s.bubbleMeta}>
            <Text style={s.bubbleTime}>
              {new Date(item.created_at_utc).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Text>
            {isUser && item.status === 'sending' && <Ionicons name="time-outline" size={12} color={colors.textSecondary} />}
            {isUser && item.status === 'sent' && <Ionicons name="checkmark-done-outline" size={12} color={colors.primary} />}
            {isUser && item.status === 'failed' && <Ionicons name="alert-circle-outline" size={12} color={colors.danger} />}
          </View>
        </View>
      </View>
    );
  }, [s, colors]);

  const statusText = status === 'connecting'
    ? `Connecting… ${session?.queue_position ? `#${session.queue_position} in queue` : ''}`
    : status === 'active'
      ? `${operatorName || 'Support Agent'}${operatorTyping ? ' is typing…' : ''}`
      : status === 'closed'
        ? 'Session ended'
        : '';

  return (
    <KeyboardAvoidingView
      style={[s.container, { paddingTop: insets.top }]}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={0}
    >
      {/* Header */}
      <View style={s.header}>
        <TouchableOpacity onPress={handleBack} style={s.headerBtn} accessibilityRole="button" accessibilityLabel="Back">
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <View style={s.headerCenter}>
          <Text style={s.headerTitle}>Support</Text>
          {statusText ? <Text style={s.headerSub}>{statusText}</Text> : null}
        </View>
        {(status === 'active' || status === 'connecting') && (
          <TouchableOpacity onPress={handleEndSession} style={s.headerBtn} accessibilityRole="button" accessibilityLabel="End support session">
            <Ionicons name="close-circle-outline" size={24} color={colors.danger} />
          </TouchableOpacity>
        )}
      </View>

      {/* Pending skeleton */}
      {status === 'connecting' && messages.length === 0 && (
        <View style={s.pendingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={s.pendingText}>Connecting you with support…</Text>
          {session?.ticket_id && <Text style={s.ticketText}>Ticket: {session.ticket_id}</Text>}
        </View>
      )}

      {/* Messages */}
      <FlatList
        ref={flatListRef}
        data={messages}
        renderItem={renderMessage}
        keyExtractor={(item, index) => `${item.id || index}`}
        contentContainerStyle={s.messageList}
        onContentSizeChange={() => flatListRef.current?.scrollToEnd({ animated: false })}
      />

      {/* Input (only when active or connecting/pending) */}
      {(status === 'active' || status === 'connecting') && (
        <View style={[s.inputRow, { paddingBottom: Math.max(insets.bottom, 8) }]}>
          <TextInput
            style={s.input}
            placeholder="Type a message…"
            placeholderTextColor={colors.textSecondary}
            value={inputText}
            onChangeText={setInputText}
            multiline
            maxLength={2000}
            onSubmitEditing={handleSend}
            blurOnSubmit={false}
          />
          <TouchableOpacity
            onPress={handleSend}
            style={[s.sendBtn, !inputText.trim() && s.sendBtnDisabled]}
            disabled={!inputText.trim()}
          >
            <Ionicons name="send" size={20} color={inputText.trim() ? '#fff' : colors.textSecondary} />
          </TouchableOpacity>
        </View>
      )}

      {/* Closed state */}
      {status === 'closed' && (
        <View style={[s.closedBanner, { paddingBottom: Math.max(insets.bottom, 16) }]}>
          <Ionicons name="checkmark-circle" size={28} color={colors.success} />
          <Text style={s.closedText}>Session ended</Text>
          <TouchableOpacity style={s.newSessionBtn} onPress={() => { clearSupport(); openSession(); }}>
            <Text style={s.newSessionBtnText}>Start New Session</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* End session confirmation modal */}
      <Modal visible={showEndConfirm} transparent animationType="fade" onRequestClose={() => setShowEndConfirm(false)}>
        <View style={s.modalOverlay}>
          <View style={s.modalCard}>
            <Text style={s.modalTitle}>End Support Session?</Text>
            <Text style={s.modalBody}>Are you sure you want to end this support session?</Text>
            <View style={s.modalBtns}>
              <TouchableOpacity style={s.modalBtnCancel} onPress={() => setShowEndConfirm(false)}>
                <Text style={s.modalBtnCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={s.modalBtnConfirm} onPress={confirmEndSession}>
                <Text style={s.modalBtnConfirmText}>End Session</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Rating modal */}
      <Modal visible={showRating} transparent animationType="fade" onRequestClose={() => setShowRating(false)}>
        <View style={s.modalOverlay}>
          <View style={s.modalCard}>
            <Text style={s.modalTitle}>Rate Your Experience</Text>
            <Text style={s.modalBody}>How was your support experience?</Text>
            <View style={s.ratingRow}>
              {[1, 2, 3, 4, 5].map((star) => (
                <TouchableOpacity key={star} onPress={() => handleRate(star)}>
                  <Ionicons
                    name={star <= selectedRating ? 'star' : 'star-outline'}
                    size={36}
                    color={star <= selectedRating ? '#f5a623' : colors.textSecondary}
                  />
                </TouchableOpacity>
              ))}
            </View>
            <TouchableOpacity onPress={() => setShowRating(false)}>
              <Text style={s.skipText}>Skip</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
}

function createStyles(c: ThemeColors) {
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: c.background },
    header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: SPACING.md, paddingVertical: SPACING.sm, borderBottomWidth: 1, borderBottomColor: c.border },
    headerBtn: { padding: SPACING.xs },
    headerCenter: { flex: 1, marginHorizontal: SPACING.sm },
    headerTitle: { fontSize: FONT_SIZE.lg, fontWeight: '700', color: c.text },
    headerSub: { fontSize: FONT_SIZE.xs, color: c.textSecondary, marginTop: 2 },
    pendingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: SPACING.xl },
    pendingText: { fontSize: FONT_SIZE.md, color: c.textSecondary, marginTop: SPACING.md },
    ticketText: { fontSize: FONT_SIZE.sm, color: c.textSecondary, marginTop: SPACING.xs },
    messageList: { padding: SPACING.md, flexGrow: 1 },
    systemMsgRow: { alignItems: 'center', marginVertical: SPACING.sm },
    systemMsg: { fontSize: FONT_SIZE.xs, color: c.textSecondary, fontStyle: 'italic', textAlign: 'center', maxWidth: '80%' },
    bubbleRow: { marginBottom: SPACING.xs },
    bubbleRowRight: { alignItems: 'flex-end' },
    bubbleRowLeft: { alignItems: 'flex-start' },
    bubble: { maxWidth: '80%', padding: SPACING.sm, borderRadius: RADIUS.lg },
    bubbleUser: { backgroundColor: c.primary, borderBottomRightRadius: 4 },
    bubbleOperator: { backgroundColor: c.card, borderBottomLeftRadius: 4 },
    bubbleText: { fontSize: FONT_SIZE.md, lineHeight: 22 },
    bubbleTextUser: { color: '#fff' },
    bubbleTextOp: { color: c.text },
    bubbleMeta: { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', marginTop: 4, gap: 4 },
    bubbleTime: { fontSize: 10, color: c.textSecondary },
    inputRow: { flexDirection: 'row', alignItems: 'flex-end', paddingHorizontal: SPACING.md, paddingTop: SPACING.sm, borderTopWidth: 1, borderTopColor: c.border, backgroundColor: c.background },
    input: { flex: 1, backgroundColor: c.card, borderRadius: RADIUS.lg, paddingHorizontal: SPACING.md, paddingVertical: SPACING.sm, fontSize: FONT_SIZE.md, color: c.text, maxHeight: 120 },
    sendBtn: { marginLeft: SPACING.sm, backgroundColor: c.primary, borderRadius: 20, width: 40, height: 40, justifyContent: 'center', alignItems: 'center' },
    sendBtnDisabled: { backgroundColor: c.card },
    closedBanner: { alignItems: 'center', paddingVertical: SPACING.lg, borderTopWidth: 1, borderTopColor: c.border },
    closedText: { fontSize: FONT_SIZE.md, color: c.success, fontWeight: '600', marginTop: SPACING.xs },
    newSessionBtn: { marginTop: SPACING.md, backgroundColor: c.primary, paddingHorizontal: SPACING.lg, paddingVertical: SPACING.sm, borderRadius: RADIUS.md },
    newSessionBtnText: { color: '#fff', fontWeight: '600', fontSize: FONT_SIZE.md },
    modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' },
    modalCard: { backgroundColor: c.card, borderRadius: RADIUS.lg, padding: SPACING.xl, width: '85%', alignItems: 'center' },
    modalTitle: { fontSize: FONT_SIZE.lg, fontWeight: '700', color: c.text, marginBottom: SPACING.sm },
    modalBody: { fontSize: FONT_SIZE.md, color: c.textSecondary, textAlign: 'center', marginBottom: SPACING.lg },
    modalBtns: { flexDirection: 'row', gap: SPACING.md },
    modalBtnCancel: { flex: 1, paddingVertical: SPACING.sm, borderRadius: RADIUS.md, backgroundColor: c.border, alignItems: 'center' },
    modalBtnCancelText: { color: c.text, fontWeight: '600' },
    modalBtnConfirm: { flex: 1, paddingVertical: SPACING.sm, borderRadius: RADIUS.md, backgroundColor: c.danger, alignItems: 'center' },
    modalBtnConfirmText: { color: '#fff', fontWeight: '600' },
    ratingRow: { flexDirection: 'row', gap: SPACING.sm, marginBottom: SPACING.md },
    skipText: { color: c.textSecondary, fontSize: FONT_SIZE.sm },
  });
}
